import { useState } from 'react';
import { doorLogin, doorRegister, checkCode } from '../lib/api';

/**
 * The degraded door. **Required, not a nicety.** (DESIGN.md §5)
 *
 * The door is the only way into the site, which makes the joke load-bearing
 * and violates design principle 4. This is the fix: if the dialogue script
 * fails to parse, the portraits 404, or the scene throws, you get a plain
 * unstyled form. Ugly, functional, and it means a typo in a YAML file can
 * never lock the entire group out of the site the night of a screening.
 *
 * Note the literal checkbox. The bribe replaces it with a fiction, but when
 * the fiction is broken the *feature* still has to work — so "remember me"
 * survives here as the boring control it always was.
 */
export function FallbackForm({
  reason,
  onEntered,
}: {
  /** Logged, and shown quietly — whoever sees this is probably the host. */
  reason: string;
  onEntered: () => void;
}) {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [code, setCode] = useState('');
  const [nickname, setNickname] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === 'register') {
        await checkCode(code);
        await doorRegister({ code, nickname, password });
      } else {
        await doorLogin({ nickname, password, remember });
      }
      onEntered();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ne ide.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-full max-w-sm flex-col justify-center gap-4 px-5 py-10">
      <h1 className="text-lg">Ulaz</h1>

      <div className="flex gap-2 text-sm">
        <button
          type="button"
          onClick={() => setMode('login')}
          className={`rounded border px-3 py-1 ${mode === 'login' ? 'border-paper/60' : 'border-paper/20 text-paper/50'}`}
        >
          Imam nalog
        </button>
        <button
          type="button"
          onClick={() => setMode('register')}
          className={`rounded border px-3 py-1 ${mode === 'register' ? 'border-paper/60' : 'border-paper/20 text-paper/50'}`}
        >
          Imam kod
        </button>
      </div>

      <form onSubmit={submit} className="flex flex-col gap-3">
        {mode === 'register' && (
          <label className="flex flex-col gap-1 text-sm">
            Kod
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoComplete="one-time-code"
              autoCapitalize="characters"
              spellCheck={false}
              required
              className="rounded border border-paper/25 bg-transparent px-3 py-2"
            />
          </label>
        )}

        <label className="flex flex-col gap-1 text-sm">
          Ime
          <input
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            autoComplete={mode === 'login' ? 'username' : 'nickname'}
            required
            className="rounded border border-paper/25 bg-transparent px-3 py-2"
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          Šifra
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            required
            className="rounded border border-paper/25 bg-transparent px-3 py-2"
          />
        </label>

        {mode === 'login' && (
          <label className="flex items-center gap-2 text-sm text-paper/70">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
            />
            Zapamti me na ovom uređaju
          </label>
        )}

        {error && <p className="text-sm text-red-400">{error}</p>}

        <button
          type="submit"
          disabled={busy}
          className="rounded border border-paper/60 px-3 py-2 disabled:opacity-50"
        >
          {busy ? '…' : 'Uđi'}
        </button>
      </form>

      <p className="mt-2 text-xs text-paper/30">
        Scena ne radi ({reason}). Ovo je rezervni ulaz — radi isto.
      </p>
    </div>
  );
}
